import { jsx } from 'react/jsx-runtime';
import { createContext, useLayoutEffect, useMemo, useContext, useState } from 'react';
import { useIdOrProvided } from './use-id-or-provided.js';

/** Creates the context for managing the connections */
function createConnectionContext() {
    return createContext(null);
}
/** Creates a hook for consuming registered connections */
function createUseConnectionConsumer(providedContext, formatter) {
    return function useConsumer(types) {
        const context = useContext(providedContext);
        const connections = context?.connections;
        return useMemo(() => {
            if (!connections)
                return {};
            const connectionArrays = types.reduce((acc, curr) => {
                acc[curr] = [...connections[curr]];
                return acc;
            }, {});
            return formatter(connectionArrays);
        }, [connections, types]);
    };
}
/**
 * Allows a component to register that it will provide a certain connector type.
 * The caller should call the result of `useRegister` in a layout effect with the value to register.
 * Cleans up after unmounting and will skip registration if no connector provider exists.
 */
function createUseConnectionRegister(providedContext) {
    return function useRegister(type, { enabled = true } = {}) {
        const context = useContext(providedContext);
        const setConnection = context?.setConnections;
        return useMemo(() => {
            if (!setConnection || !enabled)
                return null;
            return (value) => {
                setConnection((x) => {
                    const oldSet = x[type];
                    if (oldSet.has(value))
                        return x;
                    const newSet = new Set(oldSet);
                    newSet.add(value);
                    return { ...x, [type]: newSet };
                });
                return () => {
                    setConnection((x) => {
                        const newSet = new Set(x[type]);
                        newSet.delete(value);
                        return { ...x, [type]: newSet };
                    });
                };
            };
        }, [setConnection, enabled, type]);
    };
}
/**
 * Allows a component to register that it will provide a certain connector type.
 * Assumes it will be connected to the register via an id.
 * Cleans up after unmounting and will skip registration if no connector provider exists.
 */
function createUseConnectionIdRegister(providedContext) {
    const useRegister = createUseConnectionRegister(providedContext);
    return function useIdRegister(type, { enabled = true, providedId } = {}) {
        const id = useIdOrProvided(providedId);
        const register = useRegister(type, { enabled });
        const isRegistered = !!register;
        useLayoutEffect(() => register?.(id), [id, register]);
        return useMemo(() => ({ id, isRegistered }), [id, isRegistered]);
    };
}
/** Creates a connection context provider */
function createConnector(context, types, displayName) {
    function Connector({ children }) {
        const [connections, setConnections] = useState(() => types.reduce((acc, type) => {
            acc[type] = new Set();
            return acc;
        }, {}));
        const contextValue = useMemo(() => ({ connections, setConnections }), [connections]);
        return jsx(context.Provider, { value: contextValue, children: children });
    }
    Connector.displayName = displayName;
    return Connector;
}

export { createConnectionContext, createConnector, createUseConnectionConsumer, createUseConnectionIdRegister, createUseConnectionRegister };
//# sourceMappingURL=use-connections.js.map
